import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listPettyCashFunds, createPettyCashFund, updatePettyCashFund, replenishPettyCashFund, listPettyCashTransactions, createPettyCashTransaction } from '../api';

const CATEGORY_OPTIONS = [
  { value: 'REFRESHMENTS', label: 'Refreshments' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'POSTAGE', label: 'Postage & Courier' },
  { value: 'CLEANING', label: 'Cleaning Supplies' },
  { value: 'OTHER', label: 'Other' },
];

function PettyCashManagement({ authAxios, showFormMessage, userRole }) {
  const [funds, setFunds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFundForm, setShowFundForm] = useState(false);
  const [showTxnForm, setShowTxnForm] = useState(false);
  const [showReplenish, setShowReplenish] = useState(false);
  const [replenishAmount, setReplenishAmount] = useState('');
  const [fundForm, setFundForm] = useState({ name: '', initial_balance: '', custodian: '' });
  const [txnForm, setTxnForm] = useState({ date: new Date().toISOString().slice(0, 10), description: '', amount: '', category: 'OTHER', receipt_reference: '' });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchFunds = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listPettyCashFunds(authAxios);
      setFunds(Array.isArray(data) ? data : []);
    } catch { showFormMessage('Failed to load petty cash funds.', 'error'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  const fetchTransactions = useCallback(async (fundId) => {
    try {
      const data = await listPettyCashTransactions(authAxios, { fund: fundId });
      setTransactions(Array.isArray(data) ? data : []);
    } catch { showFormMessage('Failed to load transactions.', 'error'); }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchFunds(); }, [fetchFunds]);
  useEffect(() => { if (selectedFund) fetchTransactions(selectedFund); }, [selectedFund, fetchTransactions]);

  const handleCreateFund = async (e) => {
    e.preventDefault();
    try {
      await createPettyCashFund(authAxios, fundForm);
      showFormMessage('Fund created.', 'success');
      setShowFundForm(false);
      setFundForm({ name: '', initial_balance: '', custodian: '' });
      fetchFunds();
    } catch (err) { showFormMessage(err.response?.data?.detail || 'Failed to create fund.', 'error'); }
  };

  const handleCreateTxn = async (e) => {
    e.preventDefault();
    try {
      await createPettyCashTransaction(authAxios, { ...txnForm, fund: selectedFund });
      showFormMessage('Transaction recorded.', 'success');
      setShowTxnForm(false);
      setTxnForm({ date: new Date().toISOString().slice(0, 10), description: '', amount: '', category: 'OTHER', receipt_reference: '' });
      fetchTransactions(selectedFund);
      fetchFunds();
    } catch (err) { showFormMessage(err.response?.data?.detail || 'Failed to record transaction.', 'error'); }
  };

  const handleReplenish = async () => {
    try {
      await replenishPettyCashFund(authAxios, selectedFund, replenishAmount);
      showFormMessage('Fund replenished.', 'success');
      setShowReplenish(false);
      setReplenishAmount('');
      fetchTransactions(selectedFund);
      fetchFunds();
    } catch (err) { showFormMessage(err.response?.data?.detail || 'Failed to replenish.', 'error'); }
  };

  const currentFund = useMemo(() => funds.find(f => f.id === selectedFund), [funds, selectedFund]);

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Petty Cash</h2>
          <p className="text-gray-500 text-sm mt-1">Manage petty cash funds and transactions</p>
        </div>
        {canManage && (
          <button onClick={() => setShowFundForm(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">+ New Fund</button>
        )}
      </div>

      {/* Fund Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {funds.map(f => (
          <div key={f.id} onClick={() => setSelectedFund(f.id)}
            className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all hover:shadow-md ${selectedFund === f.id ? 'ring-2 ring-indigo-500' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">{f.name}</h3>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${f.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {f.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Initial Balance:</span>
                <span className="font-medium">P {parseFloat(f.initial_balance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Current Balance:</span>
                <span className="font-bold text-indigo-600">P {parseFloat(f.current_balance || 0).toFixed(2)}</span>
              </div>
              {f.custodian_name && (
                <div className="text-xs text-gray-400 mt-1">Custodian: {f.custodian_name}</div>
              )}
            </div>
          </div>
        ))}
        {funds.length === 0 && !isLoading && (
          <div className="col-span-full text-center py-8 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">No petty cash funds. Create one to get started.</p>
          </div>
        )}
      </div>

      {/* Transactions for selected fund */}
      {selectedFund && currentFund && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{currentFund.name} – Transactions</h3>
            {canManage && (
              <div className="flex gap-2">
                <button onClick={() => setShowReplenish(true)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium">Replenish</button>
                <button onClick={() => setShowTxnForm(true)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">+ Record Spend</button>
              </div>
            )}
          </div>
          {transactions.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No transactions for this fund.</p></div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{t.date}</td>
                      <td className="px-4 py-3 text-gray-800">{t.description}</td>
                      <td className="px-4 py-3 text-gray-600">{CATEGORY_OPTIONS.find(c => c.value === t.category)?.label || t.category}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{t.receipt_reference || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium">P {parseFloat(t.amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${t.is_replenishment ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {t.is_replenishment ? 'Replenish' : 'Spend'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* New Fund Modal */}
      {showFundForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">New Petty Cash Fund</h3>
            <form onSubmit={handleCreateFund} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fund Name *</label>
                <input type="text" required value={fundForm.name} onChange={e => setFundForm({ ...fundForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Office Petty Cash" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Balance (BWP) *</label>
                <input type="number" step="0.01" required value={fundForm.initial_balance} onChange={e => setFundForm({ ...fundForm, initial_balance: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowFundForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Create Fund</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Transaction Modal */}
      {showTxnForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Petty Cash Spend</h3>
            <form onSubmit={handleCreateTxn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" required value={txnForm.date} onChange={e => setTxnForm({ ...txnForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input type="text" required value={txnForm.description} onChange={e => setTxnForm({ ...txnForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Coffee and water for office" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP) *</label>
                  <input type="number" step="0.01" required value={txnForm.amount} onChange={e => setTxnForm({ ...txnForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={txnForm.category} onChange={e => setTxnForm({ ...txnForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Reference</label>
                <input type="text" value={txnForm.receipt_reference} onChange={e => setTxnForm({ ...txnForm, receipt_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Receipt/slip number" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowTxnForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Record</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Replenish Modal */}
      {showReplenish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Replenish Fund</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP)</label>
                <input type="number" step="0.01" min="0.01" value={replenishAmount} onChange={e => setReplenishAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowReplenish(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={handleReplenish} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Replenish</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PettyCashManagement;
